import * as React from 'react'
import { Copy, Check, ChevronDown, ChevronUp } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface Props {
  label: string
  text: string
  /** Approx char count above which we collapse by default. */
  collapseAbove?: number
  /** "primary" = bigger, foreground text; "muted" = smaller, dimmer. */
  variant?: 'primary' | 'muted'
}

/**
 * Reusable prompt display: section label + copy button + auto-collapse for
 * long bodies with a "Show more / Show less" toggle. Used in JobDetail's
 * Overview and Inputs tabs.
 */
export function PromptBlock({
  label,
  text,
  collapseAbove = 280,
  variant = 'primary',
}: Props) {
  const [expanded, setExpanded] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const isLong = text.length > collapseAbove

  const handleCopy = async () => {
    // Prefer the async Clipboard API, but it only exists in secure contexts
    // (HTTPS or localhost). The prod server runs over plain HTTP, so we fall
    // back to the legacy execCommand path via a temporary textarea.
    const writeViaTextarea = (s: string): boolean => {
      try {
        const ta = document.createElement('textarea')
        ta.value = s
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.top = '0'
        ta.style.left = '0'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        return ok
      } catch {
        return false
      }
    }

    let ok = false
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text)
        ok = true
      } catch {
        ok = writeViaTextarea(text)
      }
    } else {
      ok = writeViaTextarea(text)
    }

    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  const display =
    isLong && !expanded ? text.slice(0, collapseAbove).trimEnd() + '…' : text

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <div className="flex items-center gap-1 shrink-0">
          {isLong && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? 'Collapse prompt' : 'Expand prompt'}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3 w-3 mr-1" aria-hidden="true" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3 mr-1" aria-hidden="true" />
                  Show more
                </>
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleCopy}
            aria-label="Copy prompt to clipboard"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 mr-1 text-green-600" aria-hidden="true" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3 w-3 mr-1" aria-hidden="true" />
                Copy
              </>
            )}
          </Button>
        </div>
      </div>
      <div
        className={cn(
          'rounded-md border border-border p-3',
          variant === 'primary' ? 'bg-muted/30' : 'bg-muted/20',
        )}
      >
        <p
          className={cn(
            'leading-relaxed whitespace-pre-wrap break-words',
            variant === 'primary' ? 'text-sm' : 'text-xs text-muted-foreground',
          )}
        >
          {display}
        </p>
      </div>
    </div>
  )
}
